import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { formatLinkDate } from './dependencies/linkedActivity';
import ProjectActivityForm from './ProjectActivityForm';
import ResourceAllocationTab from './ResourceAllocationTab';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import useQuery from 'Hooks/useQuery';

function EditProjectActivityModal({
    id,
    activityId,
    activity = {},
    actions = [],
    isLoading,
    modal,
    categoryId,
    updateUrl,
    storeUrl,
    deleteAttachmentUrl,
    downloadAttachmentUrl,
    onCreated,
    onSuccess,
}) {
    const { fetchProjectActivityDependenciesUrl, statuses } = useContext(ProjectPlanContext);

    // A response or failure for an activity other than the latest one requested never reaches state:
    // it stays pending, so the request for the current activity owns data, error and loading.
    const requestedActivityIdRef = useRef(null);

    const dependenciesRequest = useQuery({
        enabled: false,
        queryFn: () => {
            const requestedId = activityId;
            requestedActivityIdRef.current = requestedId;
            const isStale = () => requestedActivityIdRef.current !== requestedId;

            return axios.get(fetchProjectActivityDependenciesUrl.replace('ACTIVITY_ID', requestedId)).then(
                (response) => (isStale() ? new Promise(() => {}) : { data: { activityId: requestedId, dependencies: response.data.data } }),
                (error) => (isStale() ? new Promise(() => {}) : Promise.reject(error)),
            );
        },
    });

    useEffect(() => {
        if (modal.visible && activityId) {
            dependenciesRequest.reset();
            dependenciesRequest.refetch().catch(() => {});
        }
    }, [modal.visible, activityId]);

    const close = useCallback(() => {
        modal.hide();
    }, [modal]);

    const handleCreated = useCallback(() => {
        close();
        onCreated();
    }, [close, onCreated]);

    const extraTabs = useMemo(
        () => [
            {
                id: 'resource-allocation',
                title: $t('Resource Allocation'),
                content: () => <ResourceAllocationTab activity={activity} onCancel={close} />,
            },
        ],
        [activity, close],
    );

    const dependencies = dependenciesRequest.data && dependenciesRequest.data.activityId === activityId ? dependenciesRequest.data.dependencies : null;
    const activityLoaded = !isLoading && String(activity?.id) === String(activityId);
    const ready = activityLoaded && !!dependencies;

    const content = () => {
        if (dependenciesRequest.error && !dependenciesRequest.loading) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-p-6 tw-text-center tw-text-sm tw-text-charcoal-60">
                    <div>{$t('An error occurred, please try again')}</div>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            dependenciesRequest.reset();
                            dependenciesRequest.refetch().catch(() => {});
                        }}
                    >
                        {$t('Try again')}
                    </Button>
                </div>
            );
        }

        if (!ready) {
            return <LoadingSpinner />;
        }

        return (
            <ProjectActivityForm
                mode="edit"
                activityTabTitle={$t('Edit Activity')}
                extraTabs={extraTabs}
                dependencies={dependencies}
                activity={activity}
                actions={actions}
                categoryId={categoryId}
                updateUrl={updateUrl}
                storeUrl={storeUrl}
                deleteAttachmentUrl={deleteAttachmentUrl}
                downloadAttachmentUrl={downloadAttachmentUrl}
                onSuccess={onSuccess}
                onCreated={handleCreated}
                onCancel={close}
            />
        );
    };

    return (
        <Modal id={id} modal={modal} containerStyle={{ maxWidth: '60rem' }}>
            <ModalHeader dark close={close} subtitle={activityLoaded ? activitySummary(activity, statuses) : null}>
                <div className="tw-text-base tw-font-medium [overflow-wrap:anywhere]">
                    {activityLoaded ? $t('Edit Activity: {{name}}', { name: activity.description }) : $t('Edit Activity')}
                </div>
            </ModalHeader>
            {/* Mounted only while open, so closing discards anything staged. */}
            <div className="tw-rounded-b">{modal.visible && <div>{content()}</div>}</div>
        </Modal>
    );
}

/** The saved dates and status, so every tab shows which activity is open. */
const activitySummary = (activity, statuses) => {
    const [start, end] = [activity.start_date, activity.target_completion_date].map((date) => (date ? formatLinkDate(date) : null));
    const dates = start && end ? $t('{{start}} to {{end}}', { start, end }) : start || end;

    return [dates, statuses[activity.status || 0]].filter(Boolean).join(', ');
};

export default EditProjectActivityModal;
